import { useEffect, useMemo, useRef } from 'react';
import { useCalendarView } from '../../../components/calendar-view/providers';
import { CalendarViewOffices, EventData } from '../../../components/calendar-view/types';
import { useZoomSafeContext } from '../../../components/calendar-view/zoom/zoom-context';
import { useCalendarDeepLinkParams } from '../../../hooks/calendar/use-calendar-deep-link-params';
import { useCurrentDateQueryParam } from '../../../hooks/scheduler-v3/use-current-date-query-param';
import { scrollToPosition } from '../../../utils/calendar-helpers';

type Props = {
  calendarObject: CalendarViewOffices;
  isLoadingCalendarEvents: boolean;
};

export const CalendarAutoScroll = ({ calendarObject, isLoadingCalendarEvents }: Props) => {
  const { pixelsPerMinute } = useZoomSafeContext();
  const { scrollContainerRef } = useCalendarView(['scrollContainerRef']);
  const hasScrolledToPosition = useRef(false);
  const { deepLinkedEventId: eventId } = useCalendarDeepLinkParams();

  // prevent auto-scroll if navigating to a specific event, as the calendar will scroll to that event's position separately
  const event: EventData | undefined = useMemo(() => {
    if (!eventId || !calendarObject?.length) return undefined;

    for (const office of calendarObject) {
      for (const columnEntity of office?.columnEntities || []) {
        const foundEvent = columnEntity.events?.find((e) => e.eventId === eventId);
        if (foundEvent) {
          return foundEvent;
        }
      }
    }
    return undefined;
  }, [eventId, calendarObject]);

  // Auto-scroll calendar to position on initial load
  useEffect(() => {
    if (!isLoadingCalendarEvents && scrollContainerRef && !hasScrolledToPosition.current && !event) {
      // Delay to ensure DOM is rendered
      const timeoutId = setTimeout(() => {
        scrollToPosition(scrollContainerRef, calendarObject, pixelsPerMinute);

        hasScrolledToPosition.current = true;
      }, 500);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [event, calendarObject, isLoadingCalendarEvents, scrollContainerRef, pixelsPerMinute]);

  // Sync the currentDate query param with the calendarDate in the store
  useCurrentDateQueryParam();

  return null;
};
